/**
 * evaluateCondition.ts
 *
 * Universal JSON-logic evaluator for declarative visibility or feature conditions.
 * Supports nested AND / OR / NOT groups and comparison operators.
 */

import { getByPath } from "./getByPath";

export type Condition =
  | { and: Condition[] }
  | { or: Condition[] }
  | { eq: [string, any] }
  | { in: [string, any[]] }
  | Record<string, any>;

const normalize = (v: any) => {
  if (v === "true") return true;
  if (v === "false") return false;
  if (v === undefined) return null; 
  return v;
};

export const evaluateConditions = (
  expr: Condition | undefined,
  data: Record<string, any>
): boolean => {

  if (!expr) return true;

  // Logic groups
  if (Array.isArray((expr as any).and)) return (expr as any).and.every((c) => evaluateConditions(c, data));
  if (Array.isArray((expr as any).or)) return (expr as any).or.some((c) => evaluateConditions(c, data));
  if ((expr as any).not) return !evaluateConditions((expr as any).not, data);


  // Comparison operators
  const ops = Object.keys(expr);

  if (ops.length !== 1) return false; // invalid structure

  const op = ops[0];
  const [leftKey, rightValue] = (expr as any)[op];
  const leftVal = normalize(getByPath(data, leftKey));
  const rightVal = normalize(rightValue);

  switch (op) {
    case "eq":
      if (Array.isArray(leftVal)) {
        return leftVal.includes(rightVal)
      }

      return leftVal === rightVal;
    case "in": {
      // rightVal is guaranteed to be an array
      if (!Array.isArray(rightVal)) return false;

      // If leftVal is an array, check if *any* element of leftVal is in rightVal
      if (Array.isArray(leftVal)) {
        return leftVal.some((v: any) => rightVal.includes(v));
      }

      // Otherwise just check if the single value is in the right array
      return rightVal.includes(leftVal);
    }
    default:
      return false;
  }
};
